/**
 * App shell (SPEC §6): composes the header, track chips, chord strip, bar
 * tabs, sequencer, transport row, piano and agent drawer in a single column
 * (max 1120px) and wires the engine playhead to the sequencer.
 *
 * `mountApp()` is pure composition over injected dependencies (testable with
 * fakes). `boot()` does the real wiring for `main.ts`: load the autosaved
 * session or the demo preset, create the store / engine / controller /
 * autosave, mount the UI, dock the audition card and the built-in agent
 * panel in the drawer, register the WebMCP tool groups (`mountToolGroups`,
 * 15 tools idle / 8 recording / 7 auditioning), install the keyboard
 * shortcuts (Space, R, Cmd+Z, …), the `?debug=1` inspector and the
 * `window.__bandmate` handle used by `tests/bandmate.spec.mjs`. The
 * audio-unlock gesture (first pointerdown / keydown, and Play) is owned by
 * the controller; Stop always dismisses an open audition card.
 */
import type { Store } from '../state/store-api';
import type { AppMode } from '../types';
import { mountAgentPanel } from '../agent/panel';
import { createController, type Controller, type Engine } from '../app/controller';
import { inspectorEnabled, mountInspector } from '../dev/inspector';
import * as presets from '../music/presets';
import * as theory from '../music/theory';
import { createStore } from '../state/session';
import * as storage from '../state/storage';
import { mountToolGroups, type ToolGroupsHandle } from '../tools';
import type { ToolName } from '../tools/schemas';
import { hasTestingAPI, hasWebMCP } from '../webmcp/context';
import { registry } from '../webmcp/register';
import { mountAgentDrawer } from './agent-drawer';
import { mountAuditionCard, type AuditionCardHandle } from './audition-card';
import { mountBarTabs } from './bartabs';
import { mountChordStrip } from './chordstrip';
import { h, injectStyle } from './dom';
import { mountHeader } from './header';
import { mountPiano } from './piano';
import { mountSequencer } from './sequencer';
import { installShortcuts, type ShortcutsHandle } from './shortcuts';
import { mountTracks } from './tracks';
import { mountTransport } from './transport';

/** Shape of `ui/sequencer.ts`'s mount function (injectable for tests). */
export type MountSequencer = (
  container: HTMLElement,
  deps: { store: Store; engine: Engine },
) => { el: HTMLElement; setStep(step: number): void; dispose(): void };

/** Shape of `ui/agent-drawer.ts`'s mount function (injectable for tests). */
export type MountAgentDrawer = (container: HTMLElement, deps: { store: Store }) => { el: HTMLElement; open(): void; dispose(): void };

/** Dependencies for `mountApp()`; the sequencer and drawer mounts are optional. */
export interface AppDeps {
  store: Store;
  engine: Engine;
  controller: Controller;
  mountSequencer?: MountSequencer;
  mountAgentDrawer?: MountAgentDrawer;
}

/** Mounted app: root element, region wrappers and the optional sequencer / drawer handles. */
export interface AppHandle {
  el: HTMLElement;
  /** Region wrappers, in document order. */
  regions: Record<'header' | 'tracks' | 'chords' | 'bars' | 'sequencer' | 'transport' | 'piano' | 'drawer', HTMLElement>;
  sequencer: ReturnType<MountSequencer> | null;
  drawer: ReturnType<MountAgentDrawer> | null;
  dispose(): void;
}

const CSS = `
.bm-region{min-width:0}
.bm-region--sequencer:empty{min-height:8rem;border:1px dashed var(--border-strong);border-radius:var(--radius-l);display:grid;place-items:center;color:var(--fg-faint);font-size:.85rem}
.bm-region--sequencer:empty::after{content:"Sequencer loads here"}
.bm-region--drawer:empty{display:none}
.bm-boot-error{max-width:var(--content-max);margin:2rem auto;padding:1rem;border:1px solid var(--danger);border-radius:var(--radius-l);color:var(--fg)}
`;

/** Mount the whole UI into `container` from already-created dependencies. */
export function mountApp(container: HTMLElement, deps: AppDeps): AppHandle {
  injectStyle('bm-app-css', CSS);
  const { store, engine, controller } = deps;
  const disposers: Array<() => void> = [];

  const region = (name: string, tag: 'div' | 'section' | 'header' | 'footer' = 'div', label?: string) =>
    h(tag, { class: `bm-region bm-region--${name}`, dataset: { region: name }, 'aria-label': label });

  const regions: AppHandle['regions'] = {
    header: region('header'),
    tracks: region('tracks', 'section', 'Tracks'),
    chords: region('chords', 'section', 'Chords'),
    bars: region('bars'),
    sequencer: region('sequencer', 'section', 'Sequencer'),
    transport: region('transport'),
    piano: region('piano'),
    drawer: region('drawer', 'section', 'Agent'),
  };

  const el = h(
    'div',
    { class: 'bm-shell' },
    regions.header,
    h('main', { class: 'bm-main' }, regions.tracks, regions.chords, regions.bars, regions.sequencer, regions.transport, regions.piano),
    regions.drawer,
  );
  container.append(el);

  disposers.push(mountHeader(regions.header, { store, controller }).dispose);
  disposers.push(mountTracks(regions.tracks, { store }).dispose);
  disposers.push(mountChordStrip(regions.chords, { store, controller }).dispose);
  disposers.push(mountBarTabs(regions.bars, { store, engine, controller }).dispose);

  let sequencer: ReturnType<MountSequencer> | null = null;
  if (deps.mountSequencer) {
    try {
      sequencer = deps.mountSequencer(regions.sequencer, { store, engine });
      disposers.push(() => sequencer?.dispose());
    } catch (e) {
      console.error('[bandmate] sequencer failed to mount', e);
    }
  }
  if (sequencer) {
    const seq = sequencer;
    disposers.push(engine.onStep((step) => seq.setStep(step)));
  }

  disposers.push(mountTransport(regions.transport, { store, controller, engine }).dispose);
  disposers.push(mountPiano(regions.piano, { controller }).dispose);

  let drawer: ReturnType<MountAgentDrawer> | null = null;
  if (deps.mountAgentDrawer) {
    try {
      drawer = deps.mountAgentDrawer(regions.drawer, { store });
      disposers.push(() => drawer?.dispose());
    } catch (e) {
      console.error('[bandmate] agent drawer failed to mount', e);
    }
  }

  return {
    el,
    regions,
    sequencer,
    drawer,
    dispose() {
      for (const d of disposers.reverse()) d();
      el.remove();
    },
  };
}

/** What `boot()` hands back for debugging and teardown. */
export interface BootResult {
  store: Store;
  engine: Engine;
  controller: Controller;
  app: AppHandle;
  auditionCard: AuditionCardHandle;
  toolGroups: ToolGroupsHandle;
  agentPanel: { el: HTMLElement; dispose(): void } | null;
  shortcuts: ShortcutsHandle;
  stopAutosave: () => void;
  /** Tear everything down (tools unregistered, listeners removed, autosave flushed). */
  dispose(): void;
}

/** The test/debug handle exposed as `window.__bandmate`. */
export interface BandmateHandle {
  store: Store;
  engine: Engine;
  controller: Controller;
  /** Names currently registered (15 idle / 8 recording / 7 auditioning). */
  tools(): ToolName[];
  /** The local mirror of what was registered natively (tests probe stale handles through it). */
  registry: typeof registry;
  mode(): AppMode;
  /** Transport position (bar 0..3, step 0..15). */
  step(): { bar: number; step: number };
  /** Last playhead step delivered to the UI. */
  readonly lastStep: number;
  readonly engineReady: boolean;
  /** `Tone.getContext().state` — 'running' once audio is unlocked. */
  contextState(): string;
  hasWebMCP: boolean;
  hasTestingAPI: boolean;
  ready: true;
}

declare global {
  interface Window {
    __bandmate?: BandmateHandle;
  }
}

/** Load (or seed) the session and mount the real app into `container`. */
export async function boot(container: HTMLElement): Promise<BootResult> {
  // Tone.js and the engine are the heavy part; everything else is in the main chunk.
  const [engineMod, tone] = await Promise.all([import('../audio/engine'), import('tone')]);
  const initial = storage.load() ?? presets.createSession('demo');
  const store = createStore(initial);
  const engine = engineMod.createEngine(store) as Engine;
  const controller = createController({ store, engine, theory, storage, presets });
  const stopAutosave = storage.createAutosave(store);

  const app = mountApp(container, { store, engine, controller, mountSequencer, mountAgentDrawer });

  // One audition card for the app's lifetime, docked in the drawer. Stop (human,
  // agent or Escape) dismisses it so `audition` resolves `reason:'stopped'` even
  // when audio was never unlocked and the engine never reports playing.
  const drawerEl = app.drawer?.el ?? app.regions.drawer;
  const auditionCard = mountAuditionCard(drawerEl.querySelector<HTMLElement>('[data-audition-slot]') ?? app.regions.drawer);
  const offStop = controller.onStop?.(() => auditionCard.dismiss()) ?? (() => undefined);

  const toolGroups = mountToolGroups({
    store,
    engine,
    controller,
    auditionCard,
    downloadButton: () => controller.downloadButton(),
    playButton: () => controller.playButton(),
  });

  const panelSlot = drawerEl.querySelector<HTMLElement>('[data-agent-panel-slot]');
  const agentPanel = panelSlot ? mountAgentPanel(panelSlot) : null;

  const shortcuts = installShortcuts({ store, controller, engine });
  if (inspectorEnabled()) mountInspector();

  let lastStep = 0;
  const offStep = engine.onStep((s) => {
    lastStep = s;
  });
  const handle: BandmateHandle = {
    store,
    engine,
    controller,
    tools: () => toolGroups.current(),
    registry,
    mode: () => store.mode(),
    step: () => engine.position(),
    get lastStep() {
      return lastStep;
    },
    get engineReady() {
      return engine.ready;
    },
    contextState: () => tone.getContext().state,
    hasWebMCP: hasWebMCP(),
    hasTestingAPI: hasTestingAPI(),
    ready: true,
  };
  window.__bandmate = handle;
  console.info('[bandmate] ready', { hasWebMCP: hasWebMCP(), tools: toolGroups.current().length });

  return {
    store,
    engine,
    controller,
    app,
    auditionCard,
    toolGroups,
    agentPanel,
    shortcuts,
    stopAutosave,
    dispose() {
      offStep();
      offStop();
      toolGroups.dispose();
      agentPanel?.dispose();
      shortcuts.dispose();
      app.dispose();
      controller.dispose();
      engine.dispose();
      stopAutosave();
      if (window.__bandmate === handle) delete window.__bandmate;
    },
  };
}

/** Render a visible boot failure instead of a blank page. */
export function renderBootError(container: HTMLElement, err: unknown): void {
  injectStyle('bm-app-css', CSS);
  container.replaceChildren(
    h(
      'div',
      { class: 'bm-boot-error', role: 'alert' },
      h('h1', null, 'Bandmate could not start'),
      h('p', null, err instanceof Error ? err.message : String(err)),
      h('p', { class: 'bm-muted' }, 'Reload the page. If it keeps failing, clear the "bandmate:v1" key in localStorage.'),
    ),
  );
}
